import { useCallback } from "react";
import { useListaCidadesApi } from "../../../infra/geo-veles/lista-cidades";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../presentation/hooks/store-hooks";
import { store } from "../../../application/states/store";
import { useUIService } from "../../ui";
import { getDistinctValues } from "../../../utils";
import { useCidadeUFService } from "../cidade-uf";
import {
  resetCreateListaCidadesFormState,
  resetEditListaCidadesFormState,
  resetListaCidadesFileDataState,
  resetListasCidadesState,
  setCidadesEditListaCidadesState,
  setCreateListaCidadesFormErrorsState,
  setCreateListaCidadesFormTouchedState,
  setCreateListaCidadesIdUsuarioState,
  setCreateListaCidadesNomeState,
  setEditListaCidadesAtivaState,
  setEditListaCidadesFormErrorsState,
  setEditListaCidadesFormTouchedState,
  setEditListaCidadesIdVelesGeoListaCidadesState,
  setEditListaCidadesNomeState,
  setEditListaCidadesAddCidadeUnitariaState,
  setEditListaCidadesSelectEstadoUnitarioState,
  setEditListaCidadeAddCidadeUnitariaState,
  setEditListaCidadesRemoveCidadeState,
  setListaCidadesDistinctValuesState,
  setListaCidadesFileDataState,
  setListaCidadesFileInvalidDataListState,
  setListaCidadesFileRepeatedDataListState,
  setListaCidadesFileValidDataListState,
  setListaCidadesSelectedColumnState,
  setListaCidadesValidValuesState,
  setListasCidadesState,
  setOpenCreateListaCidadesState,
  setOpenEditListaCidadesState,
  setOpenImportFileCidadesState,
  setOpenPreviewListaCidadesState,
  setSelectedListasCidadesState,
  validateCreateListaCidadesForm,
  validateEditListaCidadesForm,
} from "../../../application/states/geo-veles";
import { IListaCidades } from "../../../application/models";
import { useLocalizacaoApi } from "../../../infra/geo-veles";
import { NormalizarNomeCidade } from "../../../utils/formatters/cidade/CidadeFormatter";

export const useListaCidadesService = () => {
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const {
    user: { currentUser },
    listaCidades: listaCidadesState,
  } = useAppSelector((state) => state);
  // Hook
  // const { openModal, closeModal } = useModalContainerService();
  const { setLoading, setAlert } = useUIService();
  const { findCidadeUFCount } = useCidadeUFService();
  // API
  const {
    createListaCidadesApi,
    findListasCidadesApi,
    updateListaCidadesApi,
    updateListaCidadesStatusApi,
    findListaCidadesByIdApi,
  } = useListaCidadesApi();

  const { getLocalizacaoBySearchTermApi } = useLocalizacaoApi();

  const getListaCidadesById = useCallback(
    async (idVelesGeoListaCidades: number): Promise<IListaCidades> => {
      try {
        const listaCidades = await findListaCidadesByIdApi({
          idVelesGeoListaCidades,
        });
        return listaCidades;
      } catch (error) {
        throw error;
      }
    },
    [findListaCidadesByIdApi],
  );

  const setupListaCidades = useCallback(async () => {
    if (currentUser) {
      try {
        const getListasCidades = await findListasCidadesApi({
          idCliente: currentUser.IdCliente,
        });

        dispatch(
          setListasCidadesState({
            listasCidades: Array.isArray(getListasCidades)
              ? getListasCidades
              : [],
          }),
        );
      } catch (error: any) {
        setAlert({
          message:
            error?.response?.data?.error ??
            "Erro ao carregar listas de Cidades",
          type: "error",
        });
      }
    }
  }, [currentUser, dispatch, setAlert, findListasCidadesApi]);

  const resetListaCidadesFileData = useCallback(() => {
    dispatch(resetListaCidadesFileDataState());
  }, [dispatch]);

  const openPreviewListaCidades = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenPreviewListaCidadesState(openForm));
    },
    [dispatch],
  );

  // Nova função para validar cidades com o banco de dados
  const validateCidadesWithDatabase = useCallback(
    async (cidades: string[]) => {
      try {
        // Separar cidades válidas (com UF) e inválidas (sem UF)
        let cidadesValidas: string[] = [];
        let cidadesInvalidas: string[] = [];
        const cidadesUfs: { cidade: string; uf: string }[] = [];

        cidades.forEach((cidade) => {
          if (cidade.includes("/")) {
            const [cidadeNome, uf] = cidade.split("/");
            const cidadeTrimmed = cidadeNome.trim();
            const ufTrimmed = uf.trim();

            // Só adiciona se ambos cidade e UF não estão vazios
            if (cidadeTrimmed && ufTrimmed) {
              cidadesValidas.push(cidade);
              cidadesUfs.push({ cidade: cidadeTrimmed, uf: ufTrimmed });
            } else {
              cidadesInvalidas.push(cidade);
            }
          } else {
            // Cidade sem UF é inválida
            cidadesInvalidas.push(cidade);
          }
        });

        // Só chama a API se houver cidades válidas
        if (cidadesUfs.length > 0) {
          const cidades = await findCidadeUFCount({ cidadesUfs });

          if (cidades.todas > 0) {
            cidadesValidas = cidades.validas;
            cidadesInvalidas = cidades.invalidas;
          }

          // Aguardar um pouco para o Redux processar a resposta da API
          await new Promise((resolve) => setTimeout(resolve, 100));

          // Retornar dados reclassificados
          return {
            validas: cidadesValidas,
            invalidas: cidadesInvalidas,
          };
        }

        // Retornar dados separados (sem API)
        return {
          validas: cidadesValidas,
          invalidas: cidadesInvalidas,
        };
      } catch (error) {
        return {
          validas: [],
          invalidas: cidades,
        };
      }
    },
    [findCidadeUFCount],
  );

  const closeEditListaCidadesForm = useCallback(() => {
    dispatch(setOpenEditListaCidadesState(false));
    dispatch(resetEditListaCidadesFormState());
  }, [dispatch]);

  const getEstadoValue = (item: any): string => {
    const estadoKey = Object.keys(item).find((key) =>
      key.toLowerCase().includes("estado"),
    );

    if (estadoKey) {
      return item[estadoKey]?.toString().toLowerCase() || "";
    }

    return "";
  };

  const selectColumn = useCallback(
    async (data: { selectedColumn: string; data?: any[] }) => {
      const listaTratada = data.data?.map((item: any) => {
        const cidade = item[data.selectedColumn] || "";
        const estado = getEstadoValue(item);
        return cidade;
      });

      const loadingId = Math.random().toString();

      setLoading({
        id: loadingId,
        loading: true,
        message: "Selecionando coluna...",
      });

      dispatch(
        setListaCidadesSelectedColumnState({
          selectedColumn: data.selectedColumn,
        }),
      );

      let dataToUse = listaCidadesState?.fileData?.data ?? [];
      if (data.data) {
        dataToUse = data.data;
      }

      const combinedValues = dataToUse.map((item: any) => {
        const cidade = item[data.selectedColumn] || "";
        const estado = getEstadoValue(item);
        return `${cidade}/${estado}`.toLowerCase();
      });

      const distinctValues = getDistinctValues(combinedValues);

      dispatch(
        setListaCidadesDistinctValuesState({
          distinctValues: distinctValues.length,
        }),
      );

      const duplicatesInFile: string[] = [];
      const cityCount: { [key: string]: number } = {};

      combinedValues.forEach((cidade: string) => {
        cityCount[cidade] = (cityCount[cidade] || 0) + 1;
      });

      Object.keys(cityCount).forEach((cidade) => {
        if (cityCount[cidade] > 1) {
          duplicatesInFile.push(cidade);
        }
      });

      // Filtra removendo as que já existem na lista atual
      const filteredList = [...distinctValues].filter((item) => {
        return !listaCidadesState.editListaCidadesForm.fields.cidades.includes(
          String(item),
        );
      });

      const repeatedList = [...distinctValues].filter((item) => {
        return (
          listaCidadesState.editListaCidadesForm.fields.cidades.includes(
            String(item),
          ) || duplicatesInFile.includes(String(item))
        );
      });

      // Dispatch das repetidas (cidades que já existem no arquivo ou na lista atual)
      dispatch(
        setListaCidadesFileRepeatedDataListState({
          value: repeatedList.map((cidade) => String(cidade)),
        }),
      );

      if (filteredList.length > 0) {
        setLoading({
          id: loadingId,
          loading: true,
          message: "Validando cidades no banco de dados...",
        });

        try {
          // Chama a API de validação (que usa o findCidadeUFCount)
          const resultadoValidacao = await validateCidadesWithDatabase(
            filteredList.map((cidade) => String(cidade)),
          );

          const validValues = resultadoValidacao.validas; // Ex: ["NAO ME TOQUE/RS", "SANTA CECILIA DO SUL/RS"]
          const invalidValues = resultadoValidacao.invalidas; // Ex: ["JURUBATUBA DE MORANGUINHO MALANDRO/RS"]
          const repetidasValidas = validValues.filter((cidade) =>
            listaCidadesState.editListaCidadesForm.fields.cidades.includes(
              String(cidade),
            ),
          );

          const novasValidas = validValues.filter(
            (cidade) =>
              !listaCidadesState.editListaCidadesForm.fields.cidades.includes(
                String(cidade),
              ),
          );

          // Dispatch das cidades válidas (novas)
          dispatch(
            setListaCidadesFileValidDataListState({
              value: novasValidas,
            }),
          );

          // Dispatch das cidades inválidas
          dispatch(
            setListaCidadesFileInvalidDataListState({
              value: invalidValues,
            }),
          );

          // Dispatch das cidades repetidas que são válidas (existem no sistema e na lista atual)
          dispatch(
            setListaCidadesFileRepeatedDataListState({
              value: repetidasValidas,
            }),
          );

          // Atualizar o estado de quantidade de válidas
          dispatch(
            setListaCidadesValidValuesState({
              validValues: validValues.length,
            }),
          );

          // Se houver cidades válidas, abre o preview
          if (validValues.length > 0) {
            setTimeout(() => {
              openPreviewListaCidades(true);
            }, 100);
          }

          // Se houver cidades inválidas, mostrar alerta
          if (invalidValues.length > 0) {
            setAlert({
              message: `${invalidValues.length} cidades não foram encontradas no sistema e serão ignoradas.`,
              type: "warning",
            });
          }
        } catch (error) {
          console.error("Erro na validação de cidades:", error);
          // Em caso de erro, todas as filtradas vão para inválidas
          dispatch(
            setListaCidadesFileInvalidDataListState({
              value: filteredList.map((cidade) => String(cidade)),
            }),
          );
        }
      }

      setLoading({
        id: loadingId,
        loading: false,
      });
    },
    [
      dispatch,
      listaCidadesState.editListaCidadesForm.fields.cidades,
      listaCidadesState.fileData.data,
      setLoading,
      validateCidadesWithDatabase,
      openPreviewListaCidades,
      setAlert,
    ],
  );

  const setFile = useCallback(
    (data: {
      name: string;
      size: string;
      columns: string[];
      type: string;
      data: any[];
    }) => {
      const loadingId = Math.random().toString();

      setLoading({
        id: loadingId,
        loading: true,
        message: "Lendo arquivo...",
      });

      dispatch(
        setListaCidadesFileDataState({
          fileData: {
            columns: data.columns,
            data: data.data,
            name: data.name,
            size: data.size,
            type: data.type,
          },
        }),
      );

      setLoading({
        id: loadingId,
        loading: false,
      });
    },
    [dispatch, setLoading],
  );

  const setCreateListaCidadesName = useCallback(
    (name: string) => {
      dispatch(
        setCreateListaCidadesNomeState({
          nome: name,
        }),
      );
    },
    [dispatch],
  );

  const setEditListaCidadesName = useCallback(
    (name: string) => {
      dispatch(
        setEditListaCidadesNomeState({
          nome: name,
        }),
      );
    },
    [dispatch],
  );

  const resetForm = useCallback(() => {
    dispatch(resetEditListaCidadesFormState());
  }, [dispatch]);

  const addFileListToListaCidades = useCallback(() => {
    const loadingId = Math.random().toString();

    try {
      setLoading({
        id: loadingId,
        loading: true,
        message: "Lendo arquivo...",
      });

      /* Do not insert if there is no list selected */
      if (!listaCidadesState.editListaCidadesForm.fields.cidades) {
        return;
      }
      /* Do not insert repeated values */
      const filteredList = [
        ...listaCidadesState.fileListData.fileListTableValues.fileListValidData,
      ].filter((item) => {
        return !listaCidadesState.editListaCidadesForm.fields.cidades.includes(
          item,
        );
      });

      if (filteredList.length > 0) {
        dispatch(
          setCidadesEditListaCidadesState({
            cidades: [
              ...listaCidadesState.editListaCidadesForm.fields.cidades,
              ...listaCidadesState.fileListData.fileListTableValues
                .fileListValidData,
            ],
          }),
        );

        setAlert({
          message: "Lista adicionada com sucesso!",
          type: "success",
        });
      }
    } catch (error) {
      setAlert({
        message: "Erro ao ler arquivo",
        type: "error",
      });
    } finally {
      setLoading({
        id: loadingId,
        loading: false,
      });
    }
  }, [
    setLoading,
    listaCidadesState.editListaCidadesForm.fields.cidades,
    listaCidadesState.fileListData.fileListData,
    listaCidadesState.fileListData.fileListTableValues.fileListValidData,
    dispatch,
    setAlert,
  ]);

  const setEditListaCidadesFields = useCallback(
    (data: { listaCidades: IListaCidades }) => {
      dispatch(
        setSelectedListasCidadesState({
          listasCidades: data.listaCidades,
        }),
      );

      dispatch(
        setEditListaCidadesIdVelesGeoListaCidadesState({
          idVelesGeoListaCidades: data.listaCidades.IdVelesGeoListaCidade,
        }),
      );

      dispatch(
        setEditListaCidadesNomeState({
          nome: data.listaCidades.Nome,
        }),
      );

      dispatch(
        setEditListaCidadesAtivaState({
          ativa: data.listaCidades.Ativa,
        }),
      );

      let cidadesArray: Array<{ Cidade: string; Estado: string }> = [];

      if (data.listaCidades.Cidades) {
        try {
          const cidadesData = JSON.parse(
            data.listaCidades.Cidades as unknown as string,
          );

          if (Array.isArray(cidadesData) && cidadesData.length > 0) {
            if ("Nome" in cidadesData[0] || "UF" in cidadesData[0]) {
              cidadesArray = cidadesData.map((item: any) => ({
                Cidade: (item.Nome || "").toString().trim(),
                Estado: (item.UF || "").toString().trim(),
              }));
            } else if (
              "Cidade" in cidadesData[0] ||
              "Estado" in cidadesData[0]
            ) {
              cidadesArray = cidadesData.map((item: any) => ({
                Cidade: (item.Cidade || "").toString().trim(),
                Estado: (item.Estado || "").toString().trim(),
              }));
            } else if (typeof cidadesData[0] === "string") {
              cidadesArray = cidadesData.map((str: string) => {
                const partes = str.trim().split("");
                return {
                  Cidade: partes[0]?.trim() || "",
                  Estado: partes[1]?.trim() || "",
                };
              });
            }
          }
        } catch (error) {
          console.error("Erro ao parsear cidades:", error);
          cidadesArray = [];
        }
      }

      dispatch(
        setCidadesEditListaCidadesState({
          cidades: cidadesArray.map((c) => `${c.Cidade}/${c.Estado}`),
        }),
      );

      dispatch(
        setListaCidadesFileDataState({
          fileData: {
            name: `${data.listaCidades.Nome || "lista"}.json`,
            type: "application/json",
            size: "",
            columns: ["Cidade", "Estado"],
            data: cidadesArray,
          },
        }),
      );
    },
    [dispatch],
  );
  const selectListaCidadesToEdit = useCallback(
    (listaCidadesId: number) => {
      const listaCidadesToEdit = listaCidadesState.listasCidades.find(
        (item) => item.IdVelesGeoListaCidade === listaCidadesId,
      );
      if (listaCidadesToEdit) {
        setEditListaCidadesFields({
          listaCidades: listaCidadesToEdit,
        });
        dispatch(setOpenEditListaCidadesState(true));
      }
    },
    [dispatch, listaCidadesState, setEditListaCidadesFields],
  );

  const updateListaCidades = useCallback(async () => {
    const loadingId = Math.random().toString();
    try {
      const errors = validateEditListaCidadesForm(
        listaCidadesState.editListaCidadesForm.fields,
      );
      if (errors.length > 0) {
        dispatch(
          setEditListaCidadesFormErrorsState({
            errors: errors,
          }),
        );

        dispatch(
          setEditListaCidadesFormTouchedState({
            touched: true,
          }),
        );
      } else {
        setLoading({
          id: loadingId,
          loading: true,
          message: "Atualizando lista...",
        });

        await updateListaCidadesApi({
          idVelesGeoListaCidade:
            listaCidadesState.editListaCidadesForm.fields
              .idVelesGeoListaCidades,
          idUsuario: currentUser?.IdUsuario || 0,
          cidade: JSON.stringify(
            listaCidadesState.editListaCidadesForm.fields.cidades
              .map((str) => {
                const [cidade, uf] = str.split("/");
                return {
                  Cidade: cidade?.trim() || "",
                  Estado: uf?.trim() || "",
                };
              })
              .filter((item) => item.Cidade && item.Estado),
          ),
          nome: listaCidadesState.editListaCidadesForm.fields.nome,
        });

        setAlert({
          message: "Lista atualizada com sucesso",
          type: "success",
        });
        closeEditListaCidadesForm();
        setupListaCidades();
      }
    } catch (error: any) {
      setAlert({
        message: error.response.data.error,
        type: "error",
      });
    } finally {
      setLoading({
        id: loadingId,
        loading: false,
      });
    }
  }, [
    listaCidadesState.editListaCidadesForm.fields,
    dispatch,
    setLoading,
    updateListaCidadesApi,
    currentUser?.IdUsuario,
    setAlert,
    closeEditListaCidadesForm,
    setupListaCidades,
  ]);

  const listaCidadesRemoveCidade = useCallback(
    (cidade: string) => {
      dispatch(
        setEditListaCidadesRemoveCidadeState({
          cidade: cidade,
        }),
      );

      setAlert({
        message: "Cidade removida com sucesso",
        type: "success",
      });
    },
    [dispatch, setAlert],
  );

  const createListaCidades = useCallback(async () => {
    if (currentUser) {
      const loadingId = Math.random().toString();
      try {
        const errors = validateCreateListaCidadesForm(
          listaCidadesState.createListaCidadesForm.fields,
        );

        if (errors.length > 0) {
          dispatch(
            setCreateListaCidadesFormErrorsState({
              errors: errors,
            }),
          );

          dispatch(
            setCreateListaCidadesFormTouchedState({
              touched: true,
            }),
          );
        } else {
          setLoading({
            id: loadingId,
            loading: true,
            message: "Criando lista...",
          });
          const createListaCidades = await createListaCidadesApi({
            IdUsuario: currentUser.IdUsuario,
            Nome: listaCidadesState.createListaCidadesForm.fields.nome,
          });

          dispatch(
            setListasCidadesState({
              listasCidades: [
                ...listaCidadesState.listasCidades,
                createListaCidades,
              ],
            }),
          );

          dispatch(setOpenCreateListaCidadesState(false));
          // Define a nova lista como selecionada
          dispatch(
            setSelectedListasCidadesState({
              listasCidades: createListaCidades,
            }),
          );

          // Preenche o formulário de edição com os dados da nova lista
          setEditListaCidadesFields({ listaCidades: createListaCidades });

          // Abre o formulário de edição
          dispatch(setOpenEditListaCidadesState(true));

          // Limpa o formulário de criação
          dispatch(resetCreateListaCidadesFormState());

          setAlert({
            message: "Lista criada com sucesso",
            type: "success",
          });
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error,
          type: "error",
        });
      } finally {
        setLoading({
          id: loadingId,
          loading: false,
        });
      }
    }
  }, [
    currentUser,
    listaCidadesState,
    dispatch,
    setLoading,
    createListaCidadesApi,
    setAlert,
  ]);

  const updateListaCidadesStatus = useCallback(
    async (data: {
      IdVelesGeoListaCidade: number;
      idUsuario: number;
      status: boolean;
    }) => {
      try {
        await updateListaCidadesStatusApi({
          idVelesGeoListaCidade: data.IdVelesGeoListaCidade,
          idUsuario: data.idUsuario,
          ativa: data.status,
        });

        if (data.status === false) {
          setAlert({
            message: "Lista excluída com sucesso",
            type: "success",
          });

          await setupListaCidades();
        }
      } catch (error: any) {
        setAlert({
          message: error.response.data.error,
          type: "error",
        });
      }
    },
    [setAlert, setupListaCidades, updateListaCidadesStatusApi],
  );

  const openCreateListaCidadesForm = useCallback(
    (openForm: boolean) => {
      if (openForm) {
        if (currentUser) {
          dispatch(
            setCreateListaCidadesIdUsuarioState({
              idUsuario: currentUser.IdUsuario,
            }),
          );
        }
      } else {
        dispatch(resetCreateListaCidadesFormState());
      }
      dispatch(setOpenCreateListaCidadesState(openForm));
    },
    [dispatch, currentUser],
  );

  const openImportFileListaCidades = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenImportFileCidadesState(openForm));
    },
    [dispatch],
  );

  const openEditListaCidade = useCallback(
    (openForm: boolean) => {
      dispatch(setOpenEditListaCidadesState(openForm));
    },
    [dispatch],
  );

  const resetListasCidades = useCallback(() => {
    dispatch(resetListasCidadesState());
  }, [dispatch]);

  const verifyListaCidadesFileData = useCallback(
    (showAlerts: boolean = true) => {
      const loadingId = Math.random().toString();

      if (showAlerts) {
        setLoading({
          id: loadingId,
          loading: true,
          message: "Verificando se as cidades do arquivo são válidas",
        });
      }

      // Verificar se há dados brutos do arquivo
      const fileData = listaCidadesState.fileData.data;
      const fileColumns = listaCidadesState.fileData.columns;

      if (!fileData || fileData.length === 0) {
        if (showAlerts) {
          setLoading({
            id: loadingId,
            loading: false,
          });
          setAlert({
            message: "Nenhum arquivo foi importado",
            type: "error",
          });
        }
        return false;
      }

      if (!fileColumns || fileColumns.length === 0) {
        if (showAlerts) {
          setLoading({
            id: loadingId,
            loading: false,
          });
          setAlert({
            message: "Arquivo não possui colunas válidas",
            type: "error",
          });
        }
        return false;
      }

      if (showAlerts) {
        setLoading({
          id: loadingId,
          loading: false,
        });
        setAlert({
          message: `Arquivo importado com ${fileData.length} registros e ${fileColumns.length} colunas`,
          type: "success",
        });
      }
      return true;
    },
    [
      listaCidadesState.fileData.data,
      listaCidadesState.fileData.columns,
      setLoading,
      setAlert,
    ],
  );

  const setAddCidade = useCallback(
    (cidade: string) => {
      dispatch(
        setEditListaCidadesAddCidadeUnitariaState({
          cidadeUnitaria: cidade,
        }),
      );
    },
    [dispatch],
  );

  const setSelectUf = useCallback(
    (uf: string) => {
      dispatch(
        setEditListaCidadesSelectEstadoUnitarioState({
          estadoUnitario: uf,
        }),
      );
    },
    [dispatch],
  );

  const setAddCidadeUnitaria = useCallback(
    async (data: { cidade: string; uf: string }) => {
      const validarSeCidadeExisteNaLista =
        listaCidadesState.editListaCidadesForm.fields.cidades.some(
          (cidade) =>
            cidade.toLowerCase() === `${data.cidade}/${data.uf}`.toLowerCase(),
        );

      if (validarSeCidadeExisteNaLista) {
        setAlert({
          message: "Cidade já existe na lista",
          type: "error",
        });
        return;
      } else {
        const cidadeTratada: string = NormalizarNomeCidade(data.cidade);
        const validarCidades = await getLocalizacaoBySearchTermApi({
          categoria: "cidade",
          searchTerm: cidadeTratada,
        });

        if (validarCidades) {
          const cidadeValida = validarCidades.find(
            (cidade) => cidade.uf === data.uf,
          );

          if (cidadeValida) {
            dispatch(
              setEditListaCidadeAddCidadeUnitariaState({
                cidade: cidadeTratada.toLocaleUpperCase(),
                estado: data.uf.toLocaleUpperCase(),
              }),
            );

            setAlert({
              message: "Cidade Adicionada com sucesso ",
              type: "success",
            });
          } else {
            setAlert({
              message: "Cidade Inválida",
              type: "error",
            });
          }
        }
      }
    },
    [dispatch, getLocalizacaoBySearchTermApi, listaCidadesState.editListaCidadesForm.fields.cidades, setAlert],
  );

  return {
    getListaCidadesById,
    resetListaCidadesFileData,
    selectColumn,
    setCreateListaCidadesName,
    setEditListaCidadesName,
    resetForm,
    addFileListToListaCidades,
    setEditListaCidadesFields,
    selectListaCidadesToEdit,
    listaCidadesRemoveCidade,
    setFile,
    setupListaCidades,
    createListaCidades,
    updateListaCidades,
    updateListaCidadesStatus,
    openCreateListaCidadesForm,
    closeEditListaCidadesForm,
    openImportFileListaCidades,
    openPreviewListaCidades,
    openEditListaCidade,
    resetListasCidades,
    validateCidadesWithDatabase,
    verifyListaCidadesFileData,
    setAddCidade,
    setSelectUf,
    setAddCidadeUnitaria,
  };
};
